import { mapErrorToMessageKey, mapFieldErrorKey } from './errorCodes';

export type AppErrorKind = 'http' | 'network' | 'timeout' | 'offline' | 'parse' | 'unknown';

export interface AppError {
  kind: AppErrorKind;
  code: string;
  status?: number | undefined;
  /** Our i18n key, never a backend string. */
  messageKey: string;
  /** Decimal string captured from a known amount-limit BUSINESS_RULE. Formatted at render. */
  limitAmount?: string | undefined;
  fieldErrors?: Record<string, string> | undefined;
  requestId?: string | undefined;
  retryable: boolean;
  /** Internal only. NEVER rendered. */
  cause?: unknown;
}

export function isRetryableStatus(status: number): boolean {
  if (status === 401) return false;
  if (status === 429) return true;
  if (status === 409) return false;
  if (status >= 500) return true;
  if (status >= 400) return false;
  return false;
}

export function isRetryableKind(kind: AppErrorKind, status: number | undefined): boolean {
  if (kind === 'network' || kind === 'timeout' || kind === 'offline' || kind === 'parse') {
    return true;
  }
  if (kind === 'unknown') return false;
  if (status === undefined) return false;
  return isRetryableStatus(status);
}

export function createAppError(partial: {
  kind: AppErrorKind;
  code: string;
  status?: number | undefined;
  messageKey?: string | undefined;
  limitAmount?: string | undefined;
  fieldErrors?: Record<string, string> | undefined;
  requestId?: string | undefined;
  cause?: unknown;
}): AppError {
  const messageKey =
    partial.messageKey ?? mapErrorToMessageKey(partial.status, partial.code);
  return {
    kind: partial.kind,
    code: partial.code,
    status: partial.status,
    messageKey,
    limitAmount: partial.limitAmount,
    fieldErrors: partial.fieldErrors,
    requestId: partial.requestId,
    retryable: isRetryableKind(partial.kind, partial.status),
    cause: partial.cause,
  };
}

export function isAppError(value: unknown): value is AppError {
  if (typeof value !== 'object' || value === null) return false;
  if (!('kind' in value) || !('code' in value) || !('messageKey' in value)) return false;
  if (!('retryable' in value) || typeof value.retryable !== 'boolean') return false;
  return true;
}

/**
 * `fetch` and `XMLHttpRequest` both surface cancellation as `DOMException` named `AbortError`.
 * A timeout uses the same name, which is why the transport consults `didTimeout()` *before*
 * calling this — a timeout must be visible, an abort must not.
 */
export function isAbortedError(value: unknown): boolean {
  if (isAppError(value) && value.code === 'ABORTED') return true;
  if (typeof DOMException !== 'undefined' && value instanceof DOMException) {
    return value.name === 'AbortError';
  }
  if (value instanceof Error) return value.name === 'AbortError';
  return false;
}

export function toAppError(value: unknown): AppError {
  if (isAppError(value)) return value;
  if (isAbortedError(value)) {
    return createAppError({ kind: 'unknown', code: 'ABORTED', cause: value });
  }
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return createAppError({ kind: 'offline', code: 'OFFLINE', cause: value });
  }
  if (value instanceof TypeError) {
    return createAppError({ kind: 'network', code: 'NETWORK_ERROR', cause: value });
  }
  return createAppError({ kind: 'unknown', code: 'UNKNOWN', cause: value });
}

export function fieldErrorsFromBackend(
  fields: readonly { field: string }[] | null | undefined,
): Record<string, string> | undefined {
  if (fields === undefined || fields === null || fields.length === 0) return undefined;
  const mapped: Record<string, string> = {};
  for (const entry of fields) {
    mapped[entry.field] = mapFieldErrorKey(entry.field);
  }
  return mapped;
}
